import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@radix-ui/react-accordion';
import { ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { Checkbox } from '@components/primitives/Checkbox';
import { cn, easeOutSmooth } from '@components/primitives/utilities';

import css from './styles.module.css';

interface MultiselectTreeContextValue {
  disabled: boolean;
  value: string[];
  register(value: string): () => void;
  onChecked(value: string | string[], checked: boolean): void;
  openGroups: string[];
  isGroupOpen: boolean;
  optionLabel: string | { singular: string; plural: string };
}
const MultiselectTreeContext = createContext<MultiselectTreeContextValue>({
  disabled: false,
  value: [],
  register: () => () => {},
  onChecked: () => {},
  openGroups: [],
  isGroupOpen: false,
  optionLabel: 'option',
});

function pluralize(
  count: number,
  label: string | { singular: string; plural: string }
): ReactNode {
  if (typeof label === 'string') {
    const suffix = label.endsWith('s') ? 'es' : 's';
    return (
      <>
        <span className={css.tabular}>{count}</span> {label}
        {count !== 1 && suffix}
      </>
    );
  }
  return (
    <>
      <span className={css.tabular}>{count}</span>{' '}
      {count !== 1 ? label.plural : label.singular}
    </>
  );
}

export interface MultiselectTreeGroupProps extends PropsWithChildren {
  label: string;
  sublabel?: string;
  icon?: ReactNode;
}

export function MultiselectTreeGroup({
  children,
  label,
  sublabel,
  icon,
}: MultiselectTreeGroupProps) {
  const context = useContext(MultiselectTreeContext);
  const [groupKeys, setGroupKeys] = useState<Set<string>>(new Set());

  const selectedCount = useMemo(() => {
    return context.value.filter((value) => groupKeys.has(value)).length;
  }, [groupKeys, context.value]);

  const checked = useMemo(() => {
    if (selectedCount === 0) return false;
    if (selectedCount === groupKeys.size) return true;
    return 'indeterminate';
  }, [selectedCount, groupKeys]);

  const handleChange = useCallback(
    (checked: boolean) => {
      context.onChecked(Array.from(groupKeys), checked);
    },
    [groupKeys, context]
  );

  const register = useCallback((key: string) => {
    const unregister = context.register(key);
    setGroupKeys((prev) => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });

    return () => {
      unregister();
      setGroupKeys((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    };
  }, []);

  const value = [label, sublabel].filter(Boolean).join('|');
  const isOpen = context.openGroups.includes(value) && context.isGroupOpen;
  const groupContext = useMemo(
    () => ({ ...context, register, isGroupOpen: isOpen }),
    [context, register, isOpen]
  );

  const triggerLabel = useMemo(() => {
    return ['Toggle', label, sublabel].filter(Boolean).join(' ');
  }, [label, sublabel]);

  return (
    <MultiselectTreeContext.Provider value={groupContext}>
      <AccordionItem className={css.group} value={value}>
        <div className={css.row}>
          <div className={css.rowHeader}>
            <div className={css.rowStart}>
              <Checkbox
                className={css.checkbox}
                checked={checked}
                onChange={handleChange}
                animated={false}
                disabled={context.disabled}
              >
                <Checkbox.Label className="sr-only">{label}</Checkbox.Label>
              </Checkbox>

              <div className={css.label}>
                {icon && (
                  <span className={css.icon} aria-hidden>
                    {icon}
                  </span>
                )}
                {label}{' '}
                {sublabel && <span className={css.sublabel}>{sublabel}</span>}
              </div>

              <AccordionTrigger asChild>
                <button className={css.toggle} aria-label={triggerLabel}>
                  <ChevronRight aria-hidden />
                </button>
              </AccordionTrigger>
            </div>

            <div className={css.rowEnd}>
              <motion.p
                layout="position"
                transition={{ duration: 0.1, ease: easeOutSmooth }}
                className={css.count}
              >
                {pluralize(groupKeys.size, context.optionLabel)}
              </motion.p>

              <AnimatePresence initial={false}>
                {selectedCount > 0 && (
                  <motion.p
                    className={cn(css.count, css.selectedCount)}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 'auto', opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: easeOutSmooth }}
                  >
                    <span className={css.tabular}>{selectedCount}</span>{' '}
                    selected
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <AccordionContent forceMount asChild>
          <motion.div
            className={css.groupContent}
            initial={isOpen ? 'open' : 'closed'}
            animate={isOpen ? 'open' : 'closed'}
            variants={{
              open: {
                opacity: 1,
                height: 'auto',
                visibility: 'visible',
              },
              closed: {
                opacity: 0,
                height: 0,
                visibility: 'hidden',
              },
            }}
            transition={{ duration: 0.25, ease: easeOutSmooth }}
          >
            {children}
          </motion.div>
        </AccordionContent>
      </AccordionItem>
    </MultiselectTreeContext.Provider>
  );
}

export interface MultiselectTreeOptionProps extends PropsWithChildren {
  value: string;
  label: string;
  sublabel?: string;
  icon?: ReactNode;
}

export function MultiselectTreeOption({
  value,
  label,
  sublabel,
  icon,
  children,
}: MultiselectTreeOptionProps) {
  const context = useContext(MultiselectTreeContext);

  const checked = useMemo(() => {
    return context.value.includes(value);
  }, [value, context.value]);

  const handleChange = useCallback(
    (checked: boolean) => {
      context.onChecked(value, checked);
    },
    [value, context]
  );

  useLayoutEffect(() => {
    return context.register(value);
  }, [value, context]);

  return (
    <div className={css.row}>
      <div className={css.rowHeader}>
        <Checkbox
          className={css.checkbox}
          checked={checked}
          onChange={handleChange}
          animated={false}
          disabled={context.disabled}
        >
          <Checkbox.Label className={css.label}>
            {icon && (
              <span className={css.icon} aria-hidden>
                {icon}
              </span>
            )}
            {label}{' '}
            {sublabel && <span className={css.sublabel}>{sublabel}</span>}
          </Checkbox.Label>
        </Checkbox>
      </div>

      {children && <div className={css.rowContent}>{children}</div>}
    </div>
  );
}

export interface MultiselectTreeRootProps extends PropsWithChildren {
  value: string[];
  onChange(value: string[]): void;
  selectAllLabel?: ReactNode;
  optionLabel?: string | { singular: string; plural: string };
  disabled?: boolean;
}

export function MultiselectTreeRoot({
  children,
  value,
  onChange,
  selectAllLabel = 'Select all',
  optionLabel,
  disabled,
}: MultiselectTreeRootProps) {
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const [keys, setKeys] = useState<Set<string>>(new Set());

  const register = useCallback((key: string) => {
    setKeys((prev) => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });

    return () => {
      setKeys((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    };
  }, []);

  const onChecked = useCallback(
    (key: string | string[], checked: boolean) => {
      const newValues = new Set(value);
      const keys = Array.isArray(key) ? key : [key];
      if (checked) {
        keys.forEach((k) => newValues.add(k));
      } else {
        keys.forEach((k) => newValues.delete(k));
      }
      onChange(Array.from(newValues));
    },
    [value, onChange]
  );

  const context = useMemo<MultiselectTreeContextValue>(
    () => ({
      disabled: disabled ?? false,
      register,
      value,
      onChecked,
      openGroups,
      isGroupOpen: true,
      optionLabel: optionLabel ?? 'option',
    }),
    [disabled, register, value, onChecked, openGroups, optionLabel]
  );

  const checked = useMemo(() => {
    const valueSet = new Set(value);
    const selected = Array.from(keys).filter((key) => valueSet.has(key)).length;

    if (selected === 0) return false;
    if (selected === keys.size) return true;
    return 'indeterminate';
  }, [value, keys]);

  const handleChange = useCallback(
    (checked: boolean) => {
      onChecked(Array.from(keys), checked);
    },
    [keys, onChecked]
  );

  return (
    <MultiselectTreeContext.Provider value={context}>
      <Accordion
        type="multiple"
        className={css.container}
        value={openGroups}
        onValueChange={setOpenGroups}
      >
        <div className={css.row} data-select-all>
          <div className={css.rowHeader}>
            <div className={css.rowStart}>
              <Checkbox
                className={css.checkbox}
                checked={checked}
                onChange={handleChange}
                animated={false}
                disabled={context.disabled}
              >
                <Checkbox.Label className={css.label}>
                  {selectAllLabel}
                </Checkbox.Label>
              </Checkbox>
            </div>

            <div className={css.rowEnd}>
              <motion.p
                layout="position"
                transition={{ duration: 0.1, ease: easeOutSmooth }}
                className={css.count}
              >
                {pluralize(keys.size, context.optionLabel)}
              </motion.p>

              <AnimatePresence initial={false}>
                {value.length > 0 && (
                  <motion.p
                    className={cn(css.count, css.selectedCount)}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 'auto', opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: easeOutSmooth }}
                  >
                    <span className={css.tabular}>{value.length}</span> selected
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {children}
      </Accordion>
    </MultiselectTreeContext.Provider>
  );
}

export const MultiselectTree = Object.assign(MultiselectTreeRoot, {
  Option: MultiselectTreeOption,
  Group: MultiselectTreeGroup,
});
